import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import "../css/DoctorDetails.css";
import { BASE_URL } from '../config';
import { getAuthHeaders } from '../services/useAuth';
import Loader from "../dist/Loader";
import CustomButton from '../pages/CustomButton';
const DoctorDetails = () => {
  const location = useLocation();
  const { 
    fromDCSAssigned, 
    selectedState, 
    selectedEmployee, 
    tableData,
    states,
    employees
  } = location.state || {};

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { employeeId, type } = useParams();

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!employeeId) {
          throw new Error("Employee ID is required");
        }

        console.log(`Fetching doctors for employee: ${employeeId}`);
        const response = await fetch(`${BASE_URL}/api/doctors?employee_id=${employeeId}`, {
          headers: getAuthHeaders()
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || 
            `Failed to fetch doctors: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log("API Response Data:", data);

        // Keep only items that match employee id (defensive)
        const filteredData = Array.isArray(data) 
          ? data.filter(item => item.employee_id?.toString() === employeeId.toString())
          : [];

        setDoctors(filteredData);

        if (filteredData.length === 0) {
          console.warn("No doctors found for employee:", employeeId);
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message || "Failed to load doctor details");
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [employeeId, type]);

  // Helper: safe display for name (handles first only, last only or both)
  const renderName = (doctor) => {
    if (!doctor) return "";
    const first = (doctor.first_name || "").toString().trim();
    const last = (doctor.last_name || "").toString().trim();

    if (first && last) return `${first} ${last}`;
    if (first) return first;
    if (last) return last;
    // fallback to other fields
    if (doctor.name) return doctor.name;
    if (doctor.full_name) return doctor.full_name;
    return "-";
  };

  // Helper: generic fallback renderer
  const renderValue = (doctor, keys, joinWith = " ") => {
    // keys: array of key names to try in order. each key can be string or function(doctor)->value
    for (const k of keys) {
      try {
        const val = typeof k === "function" ? k(doctor) : doctor?.[k];
        if (val !== undefined && val !== null && String(val).trim() !== "") {
          return String(val);
        }
      } catch (e) {
        // ignore and continue
      }
    }
    return "-";
  };

  // Helper: date formatting, returns readable string or '-'
  const formatDate = (value) => {
    if (!value) return "-";
    // Accept already formatted strings or ISO string
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      // e.g. 29 Oct 2025 10:19 AM
      const options = { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" };
      return d.toLocaleString(undefined, options);
    }
    // fallback: return original trimmed string
    return String(value).trim() || "-";
  };

  // normalize coordinate for display
  const normalizeCoord = (val) => {
    // Accept numbers or numeric strings. Treat null/undefined/empty/"-" as missing.
    if (val === null || val === undefined) return "-";
    const s = String(val).trim();
    if (s === "" || s === "-" || s.toLowerCase() === "null") return "-";
    return s;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    console.log("Exporting to Excel...");
    // Implement actual Excel export logic here
  };

 

  if (loading) {
    return <Loader message="Loading doctor details..." />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Doctor Details</h3>
        <p>{error}</p>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="doctor-main-container">
      <div className="doctor-data-container">
       <div className="text-center mb-6 border-b border-gray-200 pb-3">
  <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-tight">
    {type ? `${type.charAt(0).toUpperCase() + type.slice(1)} Details` : 'Doctor Details'}
  </h1>

  {employeeId && (
    <p className="text-sm sm:text-base text-gray-600 mt-2">
      Employee ID:{" "}
      <span className="font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
        {employeeId}
      </span>
    </p>
  )}
</div>


        <div className="doctor-table-container">
          {doctors.length === 0 ? (
            <div className="doctor-no-data-message">
              <h3>No Doctor Data Found for Employee ID: {employeeId}</h3>
              <p>Please verify the employee ID or check back later</p>
              <button 
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Refresh Data
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table>
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th>S.No</th>
                      <th>Doctor</th>
                      <th>Category</th>
                      <th>Speciality</th>
                      <th>Qualification</th>
                      <th>City</th>
                      <th>Area</th>
                      <th>Route</th>
                      <th>Address</th>
                      <th>Geo Mapped</th>
                      <th>Geo Mapped Date</th>
                      <th>Geo Frezzed</th>
                      <th>Geo Frezzed Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.map((doctor, index) => {
                      // Compute display fields with fallbacks
                      const displayName = renderName(doctor);
                      const displayCategory = renderValue(doctor, [
                        "category",
                        (d) => d.category_rel?.name,
                      ]);
                      const displaySpeciality = renderValue(doctor, [
                        "speciality",
                        (d) => d.speciality_rel?.name,
                      ]);
                      const displayQualification = renderValue(doctor, [
                        "qualification",
                        "qualification_id"
                      ]);
                      const displayCity = renderValue(doctor, ["city", "state"]);
                      const displayArea = renderValue(doctor, ["area", "area_name"]);
                      const displayRoute = renderValue(doctor, ["route", "route_name"]);
                      const displayAddress = renderValue(doctor, [
                        "address",
                        "clinic_address",
                        "residence_address",
                        (d) => d.clinic_number,
                      ]);

                      const geomappedDateRaw = renderValue(doctor, [
                        "geomappeddate",
                        "geomapped_date",
                        "geo_mapped_date",
                        "filled_time",
                        "server_time",
                      ]);
                      const geomappedDate = geomappedDateRaw && geomappedDateRaw !== "-" ? formatDate(geomappedDateRaw) : "-";

                      const geofrezzed = renderValue(doctor, [
                        "geofrezzed",
                        "geo_frezzed",
                        "geo_frozen",
                      ]);
                      const geofrezzedDateRaw = renderValue(doctor, [
                        "geo_freezed_on",
                        "geofrezzed_date",
                        "geo_frezzed_date",
                        "frozen_time",
                        "updated_at",
                      ]);
                      const geo_freezed_on = geofrezzedDateRaw && geofrezzedDateRaw !== "-" ? formatDate(geofrezzedDateRaw) : "-";

                      // --- NEW: normalize and prepare latitude / longitude display ---
                      const latRaw = doctor.latitude ?? doctor.lat ?? null;
                      const lngRaw = doctor.longitude ?? doctor.lng ?? null;
                      const latDisplay = normalizeCoord(latRaw);
                      const lngDisplay = normalizeCoord(lngRaw);

                      // debug log for each row (you can remove this in production)
                      console.debug(`Doctor ${doctor.id ?? index} coords -> lat: ${latRaw}, lng: ${lngRaw}`);

                      // create google maps link only when both coordinates available
                      const hasBothCoords = latDisplay !== "-" && lngDisplay !== "-";
                      const mapsLink = hasBothCoords ? `https://www.google.com/maps?q=${encodeURIComponent(`${latDisplay},${lngDisplay}`)}` : null;

                      return (
                        <tr key={`${doctor.id ?? index}-${index}`}>
                          <td>{index + 1}</td>
                          <td>{displayName}</td>
                          <td>{displayCategory}</td>
                          <td>{displaySpeciality}</td>
                          <td>{displayQualification}</td>
                          <td>{displayCity}</td>
                          <td>{displayArea}</td>
                          <td>{displayRoute}</td>
                          <td>{displayAddress}</td>

                          {/* Updated Geo Mapped column: show both lat,lng or '-' */}
                          <td>
                            {latDisplay === "-" && lngDisplay === "-" ? (
                              "-"
                            ) : (
                              <>{latDisplay}, {lngDisplay}</>
                            )}
                          </td>

                          <td>{geomappedDate}</td>
                          <td>{geofrezzed === "true" ? "true" : "false"}</td>
                          <td>{geo_freezed_on}</td>
                          <td>
                            <button 
                              className="bg-red-500 text-white px-2 py-1 rounded cursor-pointer"
                              onClick={() => console.log('Reset for:', doctor.id)}
                            >
                              Reset
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="doctor-button-container">
          
          <CustomButton 
            label="Print"
            onClick={handlePrint}
            variant="print"
            disabled={doctors.length === 0}
          />
          <CustomButton 
            label="Excel Export"
            onClick={handleExportExcel}
            variant="export"
            disabled={doctors.length === 0}
          />
         
        </div>
      </div>
    </div>
  );
};

export default DoctorDetails;
